<template>
  <div
    v-if="getContentByType.component"
    class="w-screen md:max-w-screen-xl mx-auto my-14 px-4"
  >
    <component
      :is="getContentByType.component"
      v-bind="getContentByType.attributes"
    />
  </div>
</template>
<script setup lang="ts">
import { useHomepageAlgolia } from '~/composables/useHomepageAlgolia'
import { eventSectionComposableFactory } from '~/composables/algolia/states'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'

const { content = {} } = defineProps<{ content: any }>()

const { t, locale } = useI18n()
const { client: insightsClient } = useAlgoliaSearchInsights()

const { algoliaResults } =
  content.listType !== 'boxes' ? await useHomepageAlgolia() : {}
const useTopEvents = eventSectionComposableFactory(0)
const useTopGiftIdeasEvents = eventSectionComposableFactory(2)

const { data: topEvents, queryId: topEventsQueryID } =
  content.listType === 'events'
    ? useTopEvents(algoliaResults)
    : { data: [], queryId: ref(null) }
const { data: topGiftIdeasEvents, queryId: topGiftIdeasEventsQueryID } =
  content.listType === 'giftcards'
    ? useTopGiftIdeasEvents(algoliaResults)
    : { data: [], queryId: ref(null) }

const filteredGiftIdeas = computed(() => {
  if (!topGiftIdeasEvents.value || !topEvents.value) return []

  const topEventIds = new Set(
    topEvents.value.map((event) => event?.objectID).filter(Boolean)
  )

  return topGiftIdeasEvents.value
    .filter((event) => event?.objectID && !topEventIds.has(event.objectID))
    .slice(0, 10)
})

const eventsProps = computed(() => {
  const defaults = {
    collapseProducts: true,
    dataTestId: 'top-events-section',
    imageLoading: 'lazy',
    products: topEvents.value,
    gtmItemListId: GtmItemListId.GeneralPageTopEvents,
    queryId: topEventsQueryID.value,
    algoliaInsightsClient: insightsClient,
    algoliaIndexName: getTopEventListIndexName(locale.value),
    tags: [],
    title: content.title || t('home.browseTopEvents'),
    algoliaInsightsLabel: 'Top Events - Strapi',
  }

  if (content.listType === 'eventsRecommended') {
    return {
      ...defaults,
      dataTestId: 'recommended-events-section',
      products: filteredGiftIdeas.value,
      queryId: topGiftIdeasEventsQueryID.value,
      gtmItemListId: GtmItemListId.GeneralPageTopGiftIdeas,
      algoliaIndexName: getTopGiftIdeasIndexName(locale.value),
      title: content.title || t('home.recommendedForYou'),
      algoliaInsightsLabel: 'Gift Event Ideas - Strapi',
    }
  }

  return defaults
})

const getContentByType = computed(() => {
  const type = content?.listType?.toLowerCase()

  if (!type) return {}

  if (type === 'boxes') {
    return {
      component: resolveComponent('ProductBoxShowcase'),
      attributes: {
        title: content?.title || t('home.inspirationForYou'),
        isDurationVisible: content?.isDurationVisible ?? true,
        category: content?.category,
        tag: content?.tag,
        count: content?.count,
        itemsPerPage: 1000,
        showItemsOnDesktop: 8,
      },
    }
  }

  if (type === 'giftcards') {
    return {
      component: resolveComponent('ProductGiftcardEventsShowcase'),
      attributes: {
        title: content?.title || t('home.inspirationForYou'),
        isDurationVisible: content?.isDurationVisible ?? true,
      },
    }
  }

  return {
    component: resolveComponent('ProductShowcase'),
    attributes: {
      ...eventsProps.value,
      isDurationVisible: content.isDurationVisible ?? true,
    },
  }
})
</script>

<style scoped lang="postcss"></style>
